import { Link } from "react-router-dom";
import {
  Bed, Bath, Users, MapPin, Footprints, Car, Check, Loader2, MessageCircle, ExternalLink,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import PhotoGallery from "@/components/PhotoGallery";
import { useProperty } from "@/hooks/useProperties";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTenant } from "@/hooks/useTenant";
import { formatRent, tenantCurrency } from "@/lib/money";

interface Props {
  /** The slug to show; `null` = closed. */
  slug: string | null;
  onOpenChange: (open: boolean) => void;
  /** Drop a templated question about this listing back into the chat. */
  onAsk: (slug: string, title: string) => void;
  /** Raise above the z-[100] full-screen concierge overlay; off inside the inbox. */
  elevated?: boolean;
}

/**
 * Minimal in-place listing detail, a trimmed mirror of the admin
 * ListingPreviewDialog. Opens over the concierge (Sheet on mobile, Dialog on
 * desktop) so the renter never leaves the conversation. Fetches the full
 * property on demand via the shared useProperty(slug) hook.
 */
const ConciergeListingSheet = ({ slug, onOpenChange, onAsk, elevated = false }: Props) => {
  const tenant = useTenant();
  const z = elevated ? "z-[120]" : undefined;
  const isMobile = useIsMobile();
  const { data: property, isLoading } = useProperty(slug ?? undefined);
  const open = slug != null;

  const priceLabel = property
    ? formatRent(property.price, property.priceMax, tenantCurrency(tenant), !!property.priceVariesByStay)
    : "";

  const body = (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex-1 overflow-y-auto">
        {isLoading || !property ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="p-4 sm:p-5">
            {property.photos.length > 0 && (
              <PhotoGallery photos={property.photos} title={property.title} />
            )}
            <h2 className="mt-4 font-serif text-xl">{property.title}</h2>
            <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2 border-b pb-4">
              <p className="text-primary text-xl font-semibold">
                {priceLabel}
                <span className="text-[13px] font-normal text-muted-foreground">/month</span>
              </p>
              <span className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
                <MapPin className="h-4 w-4" /> {property.location}
              </span>
              <span className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
                <Bed className="h-4 w-4" /> {property.bedrooms} bd
              </span>
              <span className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
                <Bath className="h-4 w-4" /> {property.bathrooms} ba
              </span>
              <span className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
                <Users className="h-4 w-4" /> {property.maxGuests} guests
              </span>
              {property.walkingDistanceToBeachMin != null && (
                <span className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
                  <Footprints className="h-4 w-4" /> {property.walkingDistanceToBeachMin} min to beach
                </span>
              )}
              {property.parkingSpots > 0 && (
                <span className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
                  <Car className="h-4 w-4" /> {property.parkingSpots} parking
                </span>
              )}
            </div>
            {property.description && (
              <p className="mt-4 whitespace-pre-line text-[14px] leading-relaxed text-muted-foreground line-clamp-5">
                {property.description}
              </p>
            )}
            {property.amenities.length > 0 && (
              <div className="mt-4 grid grid-cols-2 gap-2">
                {property.amenities.slice(0, 6).map((a) => (
                  <div key={a} className="flex items-center gap-2 text-[13px] text-muted-foreground">
                    <Check className="h-4 w-4 shrink-0 text-primary" />
                    <span className="truncate">{a}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {property && (
        <div className="flex shrink-0 items-center gap-2 border-t bg-background p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <Button
            variant="outline"
            className="flex-1 gap-1.5 rounded-full"
            onClick={() => onAsk(property.slug, property.title)}
          >
            <MessageCircle className="h-4 w-4" /> Ask about this
          </Button>
          <Button asChild className="flex-1 gap-1.5 rounded-full">
            <Link to={`/properties/${property.slug}`}>
              <ExternalLink className="h-4 w-4" /> View full listing
            </Link>
          </Button>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" overlayClassName={z} className={cn("flex h-[92dvh] flex-col rounded-t-2xl p-0", z)}>
          <SheetTitle className="sr-only">Listing details</SheetTitle>
          {body}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        overlayClassName={z}
        className={cn("flex h-[88dvh] w-[94vw] max-w-3xl flex-col gap-0 overflow-hidden p-0", z)}
      >
        <DialogTitle className="sr-only">Listing details</DialogTitle>
        {body}
      </DialogContent>
    </Dialog>
  );
};

export default ConciergeListingSheet;
